import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, timeout } from 'rxjs/operators';
import { RetornoCartao } from 'src/app/Models/pagamento-enums';
import { DrawCreditComponent } from '../alunos/matricula-aluno/aluno-financeiro-contrato/matricula-caracteristicas-plano/cartao-credito/draw-credit/draw-credit.component';
import { API } from 'src/app/services/api';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-acordo-pagamento-publico',
  templateUrl: './acordo-pagamento-publico.component.html',
  styleUrls: ['./acordo-pagamento-publico.component.scss']
})
export class AcordoPagamentoPublicoComponent implements OnInit, OnDestroy {
  @ViewChild('drawCreditCard') drawCreditCard: DrawCreditComponent;

  private destroy$ = new Subject<void>();
  private renovacaoInterval: any;
  private diaCarregado: string = '';

  loading: boolean = true;
  erro: boolean = false;

  // Status possíveis: PENDENTE, PAGO, CANCELADO, EXPIRADO, INVALIDO
  status: string = '';
  valorOriginal: number = 0;
  valorDesconto: number = 0;
  valorFinal: number = 0;
  percentualDesconto: number = 0;
  pixCopiaECola: string = '';
  qrCodeBase64: string = '';
  parcelas: any[] = [];
  pixCopiado: boolean = false;

  // Dados do aluno
  nomeAluno: string = '';

  // Dados do cedente (escola)
  nomeUnidade: string = '';
  logoUrl: string = '';
  enderecoUnidade: string = '';
  telefoneUnidade: string = '';
  cnpjUnidade: string = '';

  // HF-02: Toggle forma de pagamento (Pix / Cartao)
  formaPagamento: string = 'Pix';
  linkPagamento: string = '';
  linkPagamentoCartao: string = '';
  metodoSelecionado: string = 'pix';
  loadingPix: boolean = false;
  linkToken: string = '';
  erroPix: string = '';

  // HF-04: Formulario de cartao embutido
  cartaoForm: FormGroup;
  cardData: any = {};
  processandoCartao: boolean = false;
  erroCartao: string = '';
  pagamentoSucesso: boolean = false;
  transacaoId: string = '';
  dataHoraPagamento: Date = null;

  // Validade do cartao — selects de mês (1-12) e ano
  meses = [
    { value: 1, label: '01' }, { value: 2, label: '02' }, { value: 3, label: '03' },
    { value: 4, label: '04' }, { value: 5, label: '05' }, { value: 6, label: '06' },
    { value: 7, label: '07' }, { value: 8, label: '08' }, { value: 9, label: '09' },
    { value: 10, label: '10' }, { value: 11, label: '11' }, { value: 12, label: '12' }
  ];
  anosValidade: number[] = [];

  // US-012: Campos de expiração vindos do backend
  linkExpirado: boolean = false;
  linkExpiraEm: string = '';

  // Parcelamento cartao
  maxParcelas: number = 1;
  opcoesParcelamento: number[] = [1];

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private fb: FormBuilder
  ) {
    this.cartaoForm = this.fb.group({
      numero:   [null, [Validators.required, Validators.minLength(16)]],
      nome:     [null, [Validators.required, Validators.maxLength(50)]],
      mes:      [null, Validators.required],
      ano:      [null, Validators.required],
      codigo:   [null, [Validators.required, Validators.pattern(/^\d{3,4}$/)]],
      parcelas: [1, Validators.required],
      informarCpfTitular: [false],
      cpfTitular: [null]
    });
    const anoAtual = new Date().getFullYear();
    this.anosValidade = Array.from({ length: 11 }, (_, i) => anoAtual + i);
  }

  ngOnInit(): void {
    // P0: atualiza cardData reativamente via valueChanges (evita chamar método no template)
    this.cartaoForm.valueChanges.pipe(
      distinctUntilChanged((a, b) =>
        a.numero === b.numero &&
        a.nome === b.nome &&
        a.mes === b.mes &&
        a.ano === b.ano &&
        a.codigo === b.codigo
      ),
      takeUntil(this.destroy$)
    ).subscribe(val => {
      this.cardData = {
        numero: (val.numero || '').replace(/\s/g, ''),
        nome: val.nome,
        validade: val.mes && val.ano ? `${val.mes.toString().padStart(2, '0')}/${val.ano}` : null,
        codigo: val.codigo
      };
    });

    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.status = 'INVALIDO';
      this.loading = false;
      return;
    }
    this.linkToken = token;
    this.diaCarregado = this.dataBrtAtual();
    this.carregarAcordo(token);
    // RF-1: Detecta mudanca de dia (BRT) e renova o QR Code silenciosamente
    this.renovacaoInterval = setInterval(() => this.verificarMudancaDia(), 60000);
  }

  ngOnDestroy(): void {
    if (this.renovacaoInterval) {
      clearInterval(this.renovacaoInterval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private carregarAcordo(token: string): void {
    const url = `${environment.baseUrl}${API.acordoCobranca.linkPublico}/${token}`;
    this.http.get<any>(url).pipe(takeUntil(this.destroy$)).subscribe(
      (response) => {
        if (!response) {
          this.status = 'INVALIDO';
        } else {
          this.status = (response.status || 'INVALIDO').toUpperCase();
          this.valorOriginal = response.valorOriginal || 0;
          this.valorDesconto = response.valorDesconto || 0;
          this.valorFinal = response.valorFinal || 0;
          this.percentualDesconto = response.percentualDesconto || 0;
          this.pixCopiaECola = response.pixCopiaECola || '';
          this.qrCodeBase64 = response.qrCodeBase64 || '';
          this.nomeAluno = response.nomeAluno || '';
          this.nomeUnidade = response.nomeUnidade || '';
          this.logoUrl = response.logoUrl || '';
          this.enderecoUnidade = response.enderecoUnidade || '';
          this.telefoneUnidade = response.telefoneUnidade || '';
          this.cnpjUnidade = response.cnpjUnidade || '';
          this.parcelas = (response.parcelas || []).map(p => ({
            descricao: p.descricao || `Parcela #${p.parcelaId}`,
            valor: p.valorOriginal || 0,
            dataVencimento: p.dataVencimento
          }));
          this.formaPagamento = response.formaPagamento || 'Pix';
          this.linkPagamento = response.linkPagamento || '';
          this.linkPagamentoCartao = response.linkPagamentoCartao || '';
          this.metodoSelecionado = this.pixCopiaECola ? 'pix' : 'cartao';
          this.maxParcelas = response.maxParcelas || 1;
          this.opcoesParcelamento = Array.from({ length: this.maxParcelas }, (_, i) => i + 1);
          this.cartaoForm.patchValue({ parcelas: 1 });

          // US-012: Capturar campos de expiração do backend e sobrescrever status se necessário
          this.linkExpirado = response.linkExpirado === true;
          this.linkExpiraEm = response.linkExpiraEm || '';
          if (this.linkExpirado && this.status === 'PENDENTE') {
            this.status = 'EXPIRADO';
          }
        }
        this.loading = false;
      },
      (error) => {
        if (error.status === 404) {
          this.status = 'INVALIDO';
        } else {
          this.erro = true;
        }
        this.loading = false;
      }
    );
  }

  isPendente(): boolean { return this.status === 'PENDENTE'; }
  isCancelado(): boolean { return this.status === 'CANCELADO'; }
  isExpirado(): boolean { return this.status === 'EXPIRADO'; }
  isPago(): boolean { return this.status === 'PAGO'; }
  isInvalido(): boolean { return this.status === 'INVALIDO'; }

  // Débito 2: getter de estado derivado (para debugging e futuros refactorings)
  get viewState(): 'loading' | 'erro' | 'invalido' | 'cancelado' | 'expirado' | 'pago' | 'sucesso' | 'pendente' {
    if (this.loading) return 'loading';
    if (this.erro) return 'erro';
    if (this.pagamentoSucesso) return 'sucesso';
    if (!this.status) return 'invalido';
    if (this.isPago()) return 'pago';
    if (this.isCancelado()) return 'cancelado';
    if (this.isExpirado()) return 'expirado';
    if (this.isInvalido()) return 'invalido';
    return 'pendente';
  }

  // RF-1: Retorna data atual no fuso horario de Brasilia (YYYY-MM-DD como string pt-BR)
  private dataBrtAtual(): string {
    return new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  }

  // RF-1: Verifica se o dia mudou e recarrega o acordo silenciosamente (sem spinner)
  private verificarMudancaDia(): void {
    if (this.status !== 'PENDENTE') return;
    const diaAtual = this.dataBrtAtual();
    if (diaAtual !== this.diaCarregado) {
      this.diaCarregado = diaAtual;
      this.carregarAcordo(this.linkToken);
    }
  }

  copiarPix(): void {
    if (navigator.clipboard && this.pixCopiaECola) {
      navigator.clipboard.writeText(this.pixCopiaECola).then(() => {
        this.pixCopiado = true;
        setTimeout(() => this.pixCopiado = false, 3000);
      });
    }
  }

  trocarParaPix(): void {
    this.erroPix = '';
    if (this.pixCopiaECola) {
      this.metodoSelecionado = 'pix';
      return;
    }

    this.loadingPix = true;
    const url = `${environment.baseUrl}${API.acordoCobranca.gerarPix}/${this.linkToken}/GerarPix`;
    this.http.post<any>(url, {}).pipe(takeUntil(this.destroy$)).subscribe(
      (response) => {
        if (response) {
          this.pixCopiaECola = response.pixCopiaECola || '';
          this.qrCodeBase64 = response.qrCodeBase64 || '';
          this.formaPagamento = response.formaPagamento || 'Pix';
          this.metodoSelecionado = 'pix';
        }
        this.loadingPix = false;
      },
      (error) => {
        console.error('Erro ao gerar Pix:', error);
        this.loadingPix = false;
        this.erroPix = error?.error?.mensagem || error?.error?.Mensagem || 'Erro ao gerar Pix. Tente novamente mais tarde.';
        this.metodoSelecionado = 'pix';
      }
    );
  }

  trocarParaCartao(): void {
    this.metodoSelecionado = 'cartao';
    this.erroCartao = '';
  }

  pagarComCartao(): void {
    if (this.cartaoForm.invalid || this.processandoCartao) return;

    this.processandoCartao = true;
    this.erroCartao = '';

    const form = this.cartaoForm.value;

    const payload: any = {
      numeroCartao: (form.numero || '').replace(/\s/g, ''),
      nomeTitular: form.nome,
      mesValidade: (form.mes - 1).toString(), // 0-indexed (backend faz +1)
      anoValidade: form.ano.toString(),
      codigoSeguranca: form.codigo,
      quantidadeParcelas: form.parcelas
    };

    // RF-09: CPF do titular do cartão (quando diferente do aluno)
    if (form.informarCpfTitular && form.cpfTitular) {
      payload.cpfTitularCartao = form.cpfTitular.replace(/\D/g, '');
    }

    const url = `${environment.baseUrl}${API.acordoCobranca.linkPublico}/${this.linkToken}/PagarCartao`;
    this.http.post<any>(url, payload).pipe(
      timeout(30000),
      takeUntil(this.destroy$)
    ).subscribe(
      (response) => {
        this.processandoCartao = false;
        if (response && response.retornoCartao === RetornoCartao.Sucesso) {
          this.pagamentoSucesso = true;
          this.status = 'PAGO';
          this.dataHoraPagamento = new Date();
          this.transacaoId = response.transacaoId || response.id || '';
        } else {
          this.erroCartao = this.mapearErroCartao(response?.retornoCartao);
        }
      },
      (error) => {
        this.processandoCartao = false;
        if (error?.name === 'TimeoutError') {
          this.erroCartao = 'Tempo esgotado. Verifique sua conexão e tente novamente.';
        } else {
          this.erroCartao = error?.error?.mensagem || 'Erro ao processar pagamento. Tente novamente.';
        }
      }
    );
  }

  // Débito 3: Map centralizado substituindo switch com magic numbers
  private readonly ERROS_CARTAO: Record<RetornoCartao, string> = {
    [RetornoCartao.Sucesso]:         '',
    [RetornoCartao.NaoAutorizado]:   'Transação não autorizada pelo banco emissor.',
    [RetornoCartao.Expirado]:        'Cartão expirado. Verifique a data de validade.',
    [RetornoCartao.Bloqueado]:       'Cartão bloqueado. Entre em contato com o banco.',
    [RetornoCartao.Timeout]:         'Tempo esgotado. Tente novamente.',
    [RetornoCartao.Cancelado]:       'Cartão cancelado. Use outro cartão.',
    [RetornoCartao.DadosInvalidos]:  'Verifique os dados do cartão e tente novamente.',
    [RetornoCartao.JaConfirmado]:    'Transação já confirmada anteriormente.',
    [RetornoCartao.LimiteExcedido]:  'Limite de transações excedido.',
    [RetornoCartao.ValidadeExpirada]:'Validade do cartão expirada.',
    [RetornoCartao.CvvInvalido]:     'Código de segurança inválido.',
  };

  private mapearErroCartao(codigo: number): string {
    return this.ERROS_CARTAO[codigo as RetornoCartao]
      ?? 'Erro ao processar pagamento. Tente novamente.';
  }
}
